<template>
  <div>
    <!-- Mockup bar -->
    <div class="mockup-bar">
      <span class="mockup-badge">MOCKUP</span>
      <span class="mockup-bar-sep">|</span>
      <span class="mockup-bar-label">ACT Assignments - Assign participants to users</span>
      <span class="mockup-bar-sep">|</span>
      <span class="mockup-view-label">View:</span>
      <div class="view-toggle top">
        <button
          class="view-btn"
          :class="{ active: viewMode === 'admin' }"
          @click="viewMode = 'admin'"
          type="button"
        >
          Admin
        </button>
        <button
          class="view-btn"
          :class="{ active: viewMode === 'user' }"
          @click="viewMode = 'user'"
          type="button"
        >
          User
        </button>
      </div>
      <router-link to="/wireframes/dashboard" class="mockup-back">Back</router-link>
    </div>

    <div class="signed-in-row">
      <span class="signed-in-pill">Signed in: {{ fixedUserId }}</span>
    </div>

    <div class="layout">
      <!-- Sidebar -->
      <aside class="sidebar">
        <div class="section">
          <div class="section-title">Visit Type</div>
          <select v-model="selectedVisitType" class="select">
            <option value="baseline">Baseline</option>
            <option value="biennial">Biennial</option>
          </select>
        </div>

        <div class="section">
          <div class="section-title">Participants</div>

          <input v-model="participantSearch" type="text" class="search" placeholder="Search participant display id..." />
          <div class="date-filter-row">
            <select v-model="participantDateMode" class="select date-mode-select">
              <option value="any">Any date</option>
              <option value="exact">Exact date</option>
              <option value="range">Date range</option>
              <option value="missing">No date</option>
            </select>
            <button class="clear-filter-btn" type="button" @click="clearDateFilter">
              Clear
            </button>
          </div>

          <input
            v-if="participantDateMode === 'exact'"
            v-model="participantDateExact"
            type="date"
            class="search search-date"
          />

          <div v-if="participantDateMode === 'range'" class="date-range-grid">
            <input
              v-model="participantDateFrom"
              type="date"
              class="search search-date"
              placeholder="From"
            />
            <input
              v-model="participantDateTo"
              type="date"
              class="search search-date"
              placeholder="To"
            />
          </div>

          <div class="participant-list">
            <div
              v-for="p in displayedParticipants"
              :key="p.participantId"
              class="participant-row"
              :class="{ active: selectedParticipant && selectedParticipant.participantId === p.participantId }"
              @click="viewMode === 'admin' && (selectedParticipant = p)"
            >
              <div class="pr-top">
                <span class="pr-id">{{ p.participantDisplayId }}</span>
                <span class="pr-date">{{ formatVisitDate(p.visitDate) }}</span>
              </div>
              <div v-if="viewMode === 'user'" class="pr-roles">
                {{ getRolesForParticipant(p.participantId, fixedUserId) }}
              </div>
            </div>

            <div v-if="displayedParticipants.length === 0" class="empty-state">
              No participants match the search.
            </div>
          </div>
        </div>
      </aside>

      <!-- Main -->
      <main class="main">
        <div class="detail-header">
          <div>
            <div class="detail-title">
              {{ viewMode === 'admin' ? 'Assignments editor' : 'My assigned participants' }}
              <span v-if="selectedParticipant" class="detail-subtle">
                - {{ selectedParticipant.participantDisplayId }}
              </span>
            </div>
            <div class="detail-sub">
              <span v-if="viewMode === 'admin'">
                Select a participant, then choose Interview and QA assignees.
              </span>
              <span v-else>My assigned participants.</span>
            </div>
          </div>
        </div>

        <div class="panel-body">
          <div v-if="viewMode === 'admin' && !selectedParticipant" class="empty-detail">
            Select a participant to edit assignments.
          </div>

          <div v-else-if="viewMode === 'admin'">
            <div class="card">
              <div class="card-title">Assign Interview and QA</div>

              <div class="field">
                <div class="field-title">Visit date</div>
                <input
                  v-model="draftVisitDate"
                  type="date"
                  class="select"
                  :disabled="!canEditAssignments"
                />
              </div>

              <div class="field">
                <div class="field-title">Interview assignees</div>
                <div class="multi-dropdown">
                  <div
                    class="multi-dropdown-trigger"
                    role="button"
                    tabindex="0"
                    :class="{ disabled: !canEditAssignments }"
                    @click="canEditAssignments && (showInterviewDropdown = !showInterviewDropdown)"
                  >
                    {{ getUsersSummary(draftInterviewUserIds) }}
                  </div>

                  <div v-if="showInterviewDropdown" class="multi-dropdown-panel">
                    <input
                      v-model="interviewUserSearch"
                      class="dropdown-search"
                      type="text"
                      placeholder="Search usernames..."
                      :disabled="!canEditAssignments"
                    />

                    <div class="multi-dropdown-list">
                      <label
                        v-for="u in filteredInterviewUsers"
                        :key="u.id"
                        class="dropdown-option"
                      >
                        <input
                          type="checkbox"
                          :value="u.id"
                          v-model="draftInterviewUserIds"
                          :disabled="!canEditAssignments"
                        />
                        <span class="user-dot" :style="{ background: u.color }"></span>
                        {{ u.username }}
                      </label>

                      <div v-if="filteredInterviewUsers.length === 0" class="dropdown-empty">
                        No users match "{{ interviewUserSearch }}".
                      </div>
                    </div>

                    <div class="multi-dropdown-actions">
                      <button
                        type="button"
                        class="dropdown-action"
                        :disabled="!canEditAssignments"
                        @click="draftInterviewUserIds = []"
                      >
                        Clear
                      </button>
                      <button
                        type="button"
                        class="dropdown-action primary"
                        @click="showInterviewDropdown = false"
                      >
                        Done
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div class="field">
                <div class="field-title">QA assignees</div>
                <div class="multi-dropdown">
                  <div
                    class="multi-dropdown-trigger"
                    role="button"
                    tabindex="0"
                    :class="{ disabled: !canEditAssignments }"
                    @click="canEditAssignments && (showQaDropdown = !showQaDropdown)"
                  >
                    {{ getUsersSummary(draftQaUserIds) }}
                  </div>

                  <div v-if="showQaDropdown" class="multi-dropdown-panel">
                    <input
                      v-model="qaUserSearch"
                      class="dropdown-search"
                      type="text"
                      placeholder="Search usernames..."
                      :disabled="!canEditAssignments"
                    />

                    <div class="multi-dropdown-list">
                      <label
                        v-for="u in filteredQaUsers"
                        :key="u.id"
                        class="dropdown-option"
                      >
                        <input
                          type="checkbox"
                          :value="u.id"
                          v-model="draftQaUserIds"
                          :disabled="!canEditAssignments"
                        />
                        <span class="user-dot" :style="{ background: u.color }"></span>
                        {{ u.username }}
                      </label>

                      <div v-if="filteredQaUsers.length === 0" class="dropdown-empty">
                        No users match "{{ qaUserSearch }}".
                      </div>
                    </div>

                    <div class="multi-dropdown-actions">
                      <button
                        type="button"
                        class="dropdown-action"
                        :disabled="!canEditAssignments"
                        @click="draftQaUserIds = []"
                      >
                        Clear
                      </button>
                      <button
                        type="button"
                        class="dropdown-action primary"
                        @click="showQaDropdown = false"
                      >
                        Done
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <button class="btn-primary" :disabled="!canEditAssignments" @click="mockSave" type="button">
                Save changes (mock)
              </button>
            </div>
          </div>

          <div v-else>
            <div class="card">
              <div class="card-title">Assigned to mallen</div>
              <div v-if="myParticipants.length === 0" class="empty-detail">
                No assigned participants for this visit type.
              </div>
              <div v-else class="my-list">
                <div v-for="p in myParticipants" :key="p.participantId" class="my-row">
                  <div>
                    <div class="my-id">{{ p.participantDisplayId }}</div>
                    <div class="my-meta">
                      {{ formatVisitDate(p.visitDate) }} | {{ getRolesForParticipant(p.participantId, fixedUserId) }}
                    </div>
                  </div>
                  <div>
                    <button class="btn-secondary" type="button" @click="goToTasks(p)">
                      Go to tasks
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ActAssignmentsMockup',
  data() {
    return {
      fixedUserId: 'mallen',
      viewMode: 'admin',
      canEditAssignments: true,
      selectedVisitType: 'baseline',

      participantSearch: '',
      participantDateMode: 'any',
      participantDateExact: '',
      participantDateFrom: '',
      participantDateTo: '',
      selectedParticipant: null,
      draftVisitDate: '',
      draftInterviewUserIds: [],
      draftQaUserIds: [],
      showInterviewDropdown: false,
      showQaDropdown: false,
      interviewUserSearch: '',
      qaUserSearch: '',

      participants: [
        { participantId: 'P-ACT-001', participantDisplayId: 'ACT001', visitType: 'baseline', visitDate: '2026-01-12' },
        { participantId: 'P-ACT-002', participantDisplayId: 'ACT002', visitType: 'baseline', visitDate: '2026-01-18' },
        { participantId: 'P-ACT-005', participantDisplayId: 'ACT005', visitType: 'baseline', visitDate: '2026-02-03' },
        { participantId: 'P-ACT-006', participantDisplayId: 'ACT006', visitType: 'baseline', visitDate: '2026-02-14' },
        { participantId: 'P-ACT-007', participantDisplayId: 'ACT007', visitType: 'baseline', visitDate: '2026-02-26' },
        { participantId: 'P-ACT-008', participantDisplayId: 'ACT008', visitType: 'baseline', visitDate: '2026-03-05' },
        { participantId: 'P-ACT-009', participantDisplayId: 'ACT009', visitType: 'baseline', visitDate: null },
        { participantId: 'P-ACT-003', participantDisplayId: 'ACT003', visitType: 'biennial', visitDate: '2026-03-19' },
        { participantId: 'P-ACT-004', participantDisplayId: 'ACT004', visitType: 'biennial', visitDate: '2026-03-25' },
        { participantId: 'P-ACT-010', participantDisplayId: 'ACT010', visitType: 'biennial', visitDate: '2026-04-02' },
        { participantId: 'P-ACT-011', participantDisplayId: 'ACT011', visitType: 'biennial', visitDate: null },
        { participantId: 'P-ACT-012', participantDisplayId: 'ACT012', visitType: 'biennial', visitDate: '2026-04-17' },
        { participantId: 'P-ACT-013', participantDisplayId: 'ACT013', visitType: 'biennial', visitDate: '2026-05-01' },
        { participantId: 'P-ACT-014', participantDisplayId: 'ACT014', visitType: 'biennial', visitDate: null }
      ],

      users: [
        { id: 'mallen', username: 'mallen', color: '#317391' },
        { id: 'nrose', username: 'nrose', color: '#7c3aed' },
        { id: 'acarrillo32', username: 'acarrillo32', color: '#d97706' },
        { id: 'kaceucr', username: 'kaceucr', color: '#0ea5e9' },
        { id: 'qcoppola', username: 'qcoppola', color: '#22c55e' }
      ],

      // participantId -> interviewUserIds/qaUserIds arrays (mock)
      assignmentState: {
        // participantId -> role-assigned userId arrays (mock)
        'P-ACT-001': { interviewUserIds: ['mallen', 'kaceucr'], qaUserIds: ['nrose'] },
        'P-ACT-002': { interviewUserIds: ['mallen', 'kaceucr'], qaUserIds: ['nrose', 'acarrillo32'] },
        'P-ACT-003': { interviewUserIds: ['mallen', 'qcoppola'], qaUserIds: ['acarrillo32'] },
        'P-ACT-004': { interviewUserIds: ['mallen'], qaUserIds: [] },
        'P-ACT-005': { interviewUserIds: ['mallen'], qaUserIds: ['nrose'] },
        'P-ACT-006': { interviewUserIds: [], qaUserIds: ['mallen'] },
        'P-ACT-010': { interviewUserIds: ['kaceucr'], qaUserIds: ['mallen'] },
        'P-ACT-012': { interviewUserIds: ['mallen'], qaUserIds: [] }
      }
    };
  },
  computed: {
    filteredInterviewUsers() {
      const q = (this.interviewUserSearch || '').trim().toLowerCase();
      if (!q) return this.users;
      return this.users.filter(u => u.username.toLowerCase().includes(q));
    },
    filteredQaUsers() {
      const q = (this.qaUserSearch || '').trim().toLowerCase();
      if (!q) return this.users;
      return this.users.filter(u => u.username.toLowerCase().includes(q));
    },
    filteredParticipants() {
      const q = (this.participantSearch || '').trim().toLowerCase();
      return this.participants
        .filter(p => p.visitType === this.selectedVisitType)
        .filter(p => this.matchesDateFilter(p.visitDate))
        .filter(p => !q || p.participantDisplayId.toLowerCase().includes(q));
    },
    myParticipants() {
      return this.filteredParticipants.filter(p => this.isUserAssignedToParticipant(this.fixedUserId, p.participantId));
    },
    displayedParticipants() {
      return this.viewMode === 'admin' ? this.filteredParticipants : this.myParticipants;
    }
  },
  watch: {
    selectedVisitType() {
      this.selectedParticipant = this.filteredParticipants[0] || null;
    },
    participantSearch() {
      if (this.viewMode === 'admin') this.selectedParticipant = this.filteredParticipants[0] || null;
    },
    participantDateMode() {
      if (this.viewMode === 'admin') this.selectedParticipant = this.filteredParticipants[0] || null;
    },
    participantDateExact() {
      if (this.viewMode === 'admin') this.selectedParticipant = this.filteredParticipants[0] || null;
    },
    participantDateFrom() {
      if (this.viewMode === 'admin') this.selectedParticipant = this.filteredParticipants[0] || null;
    },
    participantDateTo() {
      if (this.viewMode === 'admin') this.selectedParticipant = this.filteredParticipants[0] || null;
    },
    viewMode() {
      this.selectedParticipant = this.filteredParticipants[0] || null;
      this.showInterviewDropdown = false;
      this.showQaDropdown = false;
    },
    selectedParticipant() {
      this.syncDraftsFromSelectedParticipant();
    }
  },
  mounted() {
    this.selectedParticipant = this.filteredParticipants[0] || null;
    this.syncDraftsFromSelectedParticipant();
  },
  methods: {
    clearDateFilter() {
      this.participantDateMode = 'any';
      this.participantDateExact = '';
      this.participantDateFrom = '';
      this.participantDateTo = '';
    },

    matchesDateFilter(visitDate) {
      const d = visitDate || '';
      if (this.participantDateMode === 'any') return true;
      if (this.participantDateMode === 'missing') return !d;
      if (this.participantDateMode === 'exact') {
        if (!this.participantDateExact) return true;
        return d === this.participantDateExact;
      }
      if (this.participantDateMode === 'range') {
        if (!d) return false;
        if (this.participantDateFrom && d < this.participantDateFrom) return false;
        if (this.participantDateTo && d > this.participantDateTo) return false;
        return true;
      }
      return true;
    },

    syncDraftsFromSelectedParticipant() {
      if (!this.selectedParticipant) {
        this.draftVisitDate = '';
        this.draftInterviewUserIds = [];
        this.draftQaUserIds = [];
        return;
      }

      const pid = this.selectedParticipant.participantId;
      const a = this.assignmentState[pid] || {};
      this.draftVisitDate = this.selectedParticipant.visitDate || '';
      this.draftInterviewUserIds = a.interviewUserIds ? [...a.interviewUserIds] : [];
      this.draftQaUserIds = a.qaUserIds ? [...a.qaUserIds] : [];
    },

    getUserLabel(userId) {
      if (!userId) return 'Unassigned';
      const u = this.users.find(x => x.id === userId);
      return u ? u.username : userId;
    },

    getUsersSummary(userIds) {
      const ids = (userIds || []).filter(Boolean);
      if (ids.length === 0) return 'Unassigned';
      const labels = ids.map(id => this.getUserLabel(id));
      if (labels.length <= 2) return labels.join(', ');
      return `${labels.slice(0, 2).join(', ')} +${labels.length - 2} more`;
    },

    isUserAssignedToParticipant(userId, participantId) {
      const a = this.assignmentState[participantId] || {};
      const interview = a.interviewUserIds || [];
      const qa = a.qaUserIds || [];
      return interview.includes(userId) || qa.includes(userId);
    },

    getRolesForParticipant(participantId, userId) {
      const a = this.assignmentState[participantId] || {};
      const roles = [];
      if ((a.interviewUserIds || []).includes(userId)) roles.push('Interview');
      if ((a.qaUserIds || []).includes(userId)) roles.push('QA');
      return roles.length ? roles.join(', ') : 'No role';
    },

    formatVisitDate(isoDate) {
      if (!isoDate) return 'No date';
      return isoDate;
    },

    goToTasks(participant) {
      alert(`Mock: open tasks for ${participant.participantDisplayId}.`);
    },

    mockSave() {
      if (!this.selectedParticipant || !this.canEditAssignments) return;

      this.showInterviewDropdown = false;
      this.showQaDropdown = false;

      const pid = this.selectedParticipant.participantId;
      if (!this.assignmentState[pid]) this.assignmentState[pid] = { interviewUserIds: [], qaUserIds: [] };

      this.selectedParticipant.visitDate = this.draftVisitDate || null;
      this.assignmentState[pid].interviewUserIds = [...this.draftInterviewUserIds];
      this.assignmentState[pid].qaUserIds = [...this.draftQaUserIds];

      alert('Mock: Save assignments -> backend would validate portal permissions and update DynamoDB assignment items.');
    }
  }
};
</script>

<style scoped>
.mockup-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 20px;
  border-bottom: 1px solid #e5e7eb;
  font-size: 13px;
  color: #6b7280;
}
.mockup-badge {
  font-size: 10px;
  font-weight: 900;
  background: #fef3c7;
  color: #92400e;
  padding: 1px 6px;
  border-radius: 3px;
  letter-spacing: 0.05em;
}
.mockup-bar-sep { color: #d1d5db; }
.mockup-bar-label { color: #9ca3af; }
.mockup-view-label { color: #6b7280; font-weight: 900; }
.mockup-back { margin-left: auto; color: #9ca3af; text-decoration: none; font-size: 12px; font-weight: 700; }
.mockup-back:hover { color: #64748b; }

.signed-in-row {
  display: flex;
  justify-content: flex-end;
  padding: 10px 18px 0 18px;
}
.signed-in-pill {
  border: 1px solid #d1d5db;
  background: #f8fafc;
  color: #1f2937;
  font-weight: 900;
  border-radius: 999px;
  padding: 6px 12px;
  font-size: 12px;
}

.layout {
  display: grid;
  grid-template-columns: 320px 1fr;
  gap: 16px;
  padding: 18px;
}
.sidebar {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 14px;
  background: #ffffff;
  height: fit-content;
  position: sticky;
  top: 80px;
}
.section + .section { margin-top: 16px; }
.section-title {
  font-weight: 900;
  color: #111827;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
}

.view-toggle { display: flex; gap: 8px; }
.view-toggle.top { align-items: center; }
.view-btn {
  border: 1px solid #d1d5db;
  background: #fff;
  color: #374151;
  font-weight: 900;
  border-radius: 10px;
  padding: 8px 10px;
  cursor: pointer;
}
.view-btn.active {
  background: #3986A8;
  border-color: #3986A8;
  color: #fff;
}
.select, .search, .textarea {
  width: 100%;
  box-sizing: border-box;
  border-radius: 10px;
  border: 1px solid #d1d5db;
  padding: 10px 12px;
  font-size: 14px;
}
.select:focus, .search:focus, .textarea:focus {
  outline: none;
  border-color: #237091;
}
.search { margin-bottom: 12px; }
.search-date { margin-top: -4px; }
.date-filter-row {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 8px;
  margin-bottom: 8px;
}
.date-mode-select { margin-bottom: 0; }
.clear-filter-btn {
  border: 1px solid #d1d5db;
  background: #fff;
  color: #374151;
  border-radius: 10px;
  padding: 8px 10px;
  font-size: 12px;
  font-weight: 900;
  cursor: pointer;
}
.clear-filter-btn:hover { border-color: #94a3b8; }
.date-range-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}
.participant-list {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  overflow: hidden;
  background: #f9fafb;
}
.participant-row {
  padding: 12px 12px;
  cursor: pointer;
  border-bottom: 1px solid #eef2f7;
}
.participant-row:last-child { border-bottom: none; }
.participant-row:hover { background: #eef6ff; }
.participant-row.active { background: #e8f4f8; }
.pr-top { display: flex; gap: 10px; align-items: baseline; justify-content: space-between; }
.pr-id { font-weight: 900; color: #237091; }
.pr-date { font-size: 12px; color: #64748b; font-weight: 800; }
.pr-roles { margin-top: 4px; font-size: 12px; color: #6b7280; font-weight: 900; }
.empty-state {
  padding: 14px 12px;
  color: #9ca3af;
  font-weight: 900;
  font-size: 13px;
}

.main {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
  overflow: visible;
}
.detail-header {
  padding: 16px 18px;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}
.detail-title { font-size: 18px; font-weight: 900; color: #111827; }
.detail-subtle { font-size: 12px; color: #6b7280; font-weight: 900; margin-left: 6px; }
.detail-sub { font-size: 13px; color: #6b7280; margin-top: 4px; }

.panel-body { padding: 14px 18px; overflow: visible; }
.empty-detail {
  padding: 28px 0;
  text-align: center;
  color: #9ca3af;
  font-weight: 900;
}

.card {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 12px;
  background: #fff;
}
.card-title { font-weight: 900; color: #111827; margin-bottom: 10px; display: flex; gap: 10px; align-items: center; }

.user-grid { display: flex; flex-wrap: wrap; gap: 10px; }
.user-pill {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  background: #f9fafb;
  font-weight: 900;
  color: #374151;
  font-size: 13px;
}
.user-dot { width: 10px; height: 10px; border-radius: 50%; display: inline-block; }

.assign-editor-table { border: 1px solid #e5e7eb; border-radius: 10px; overflow: hidden; }
.assign-head {
  display: grid;
  grid-template-columns: 180px 1fr;
  padding: 10px 12px;
  gap: 10px;
  font-weight: 900;
  color: #6b7280;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  background: #f9fafb;
  border-bottom: 1px solid #eef2f7;
}
.assign-line {
  display: grid;
  grid-template-columns: 180px 1fr;
  padding: 10px 12px;
  gap: 10px;
  border-bottom: 1px solid #eef2f7;
  align-items: center;
}
.assign-line:last-child { border-bottom: none; }
.assign-user { font-weight: 900; color: #374151; display: flex; align-items: center; gap: 10px; }
.assign-actions { display: flex; flex-wrap: wrap; gap: 10px; }
.check { display: inline-flex; align-items: center; gap: 8px; font-weight: 900; color: #374151; font-size: 13px; }

.user-checkbox-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 10px 14px;
}
.check-user {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-weight: 900;
  color: #374151;
  font-size: 13px;
  user-select: none;
  cursor: pointer;
}
.check-user input {
  width: 16px;
  height: 16px;
  accent-color: #3986A8;
}

.btn-primary {
  width: 100%;
  padding: 10px 12px;
  border-radius: 10px;
  border: none;
  background: #3986A8;
  color: #fff;
  font-weight: 900;
  cursor: pointer;
  margin-top: 12px;
}
.btn-primary:disabled { background: #cbd5e1; cursor: not-allowed; }
.hint { margin-top: 10px; color: #6b7280; font-weight: 800; font-size: 13px; line-height: 1.35; }

.field { margin-top: 12px; }
.field:first-of-type { margin-top: 0; }
.field-title { font-weight: 900; color: #111827; font-size: 13px; display: flex; align-items: center; gap: 8px; margin-bottom: 8px; }

.multi-dropdown { position: relative; }
.multi-dropdown-trigger {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  border-radius: 10px;
  border: 1px solid #d1d5db;
  padding: 10px 12px;
  font-size: 14px;
  font-weight: 900;
  color: #374151;
  background: #fff;
  cursor: pointer;
  user-select: none;
}
.multi-dropdown-trigger.disabled {
  background: #f3f4f6;
  cursor: not-allowed;
  color: #9ca3af;
}
.multi-dropdown-panel {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  right: 0;
  z-index: 70;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #fff;
  box-shadow: 0 8px 24px rgba(0,0,0,0.12);
  padding: 10px;
  max-height: 320px;
  display: flex;
  flex-direction: column;
}
.dropdown-search {
  width: 100%;
  box-sizing: border-box;
  border-radius: 10px;
  border: 1px solid #d1d5db;
  padding: 10px 12px;
  font-size: 14px;
  margin-bottom: 8px;
}
.dropdown-search:focus { outline: none; border-color: #237091; }
.multi-dropdown-list { overflow: auto; max-height: 200px; padding-right: 4px; }
.dropdown-option {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 6px;
  border-radius: 8px;
  font-weight: 900;
  color: #374151;
  font-size: 13px;
  cursor: pointer;
}
.dropdown-option:hover { background: #f9fafb; }
.dropdown-option input { width: 16px; height: 16px; accent-color: #3986A8; }
.dropdown-empty { padding: 10px 6px; color: #9ca3af; font-weight: 900; font-size: 13px; }
.multi-dropdown-actions {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  margin-top: 10px;
}
.dropdown-action {
  border-radius: 10px;
  border: 1px solid #d1d5db;
  background: #fff;
  color: #374151;
  padding: 8px 10px;
  font-weight: 900;
  cursor: pointer;
  font-size: 13px;
  flex: 1;
}
.dropdown-action.primary {
  background: #3986A8;
  border-color: #3986A8;
  color: #fff;
}
.dropdown-action:disabled { background: #f3f4f6; cursor: not-allowed; color: #9ca3af; border-color: #e5e7eb; }

.my-list { display: flex; flex-direction: column; gap: 10px; }
.my-row {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 10px 12px;
  background: #f9fafb;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.my-id { font-weight: 900; color: #237091; }
.my-meta { margin-top: 3px; font-size: 12px; color: #6b7280; font-weight: 900; }
.btn-secondary {
  border: 1px solid #3986A8;
  background: #3986A8;
  color: #fff;
  font-weight: 900;
  border-radius: 10px;
  padding: 8px 10px;
  font-size: 12px;
  cursor: pointer;
  white-space: nowrap;
}
.btn-secondary:hover {
  border-color: #237091;
  background: #237091;
  color: #fff;
}

@media (max-width: 1000px) {
  .layout { grid-template-columns: 1fr; }
  .sidebar { position: relative; top: 0; }
}
</style>

